import React, { Component, createRef, RefObject } from "react";
import { connect } from "react-redux";
import { Location } from "history";

import jsonp from "jsonp";
import cx from "classnames";

import { Image, Button, TextField } from "components/base";

import { GlobalState, GlobalSettings, Status } from "types";

import { MinniemuseShopRoutes, ExternalRoutes } from "constants/Routes";

interface Props {
  globalSettings: GlobalSettings;
  location: Location;
}

interface State {
  emailAddress: string;
  status: Status;
  responseMsg: Error | string | null;
  showErrorMsg: boolean;
  showSuccessMsg: boolean;
  footerIsActive: boolean;
}

class Footer extends Component<Props, State> {
  state: State = {
    emailAddress: "",
    status: Status.IDLE,
    responseMsg: null,
    showErrorMsg: false,
    showSuccessMsg: false,
    footerIsActive: false
  };

  footerRef = createRef<HTMLDivElement>();

  componentDidMount() {
    window.addEventListener("scroll", this.scrollCheck);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollCheck);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { status, responseMsg } = this.state;

    if (prevProps.location.pathname !== this.props.location.pathname) {
      setTimeout(() => this.setState({ footerIsActive: false }), 0);
    }

    if (prevState.status === Status.PENDING && status === Status.FULFILLED) {
      this.setState({
        emailAddress: "",
        showSuccessMsg: true,
        showErrorMsg: false
      });
      setTimeout(() => this.setState({ showSuccessMsg: false }), 3000);
    }

    if (prevState.status === Status.PENDING && status === Status.REJECTED) {
      this.setState({ showErrorMsg: true, showSuccessMsg: false });
      setTimeout(() => this.setState({ showErrorMsg: false }), 3000);
    }

    if (prevState.responseMsg !== responseMsg) {
      console.log(responseMsg);
    }
  }

  subscribe = () => {
    if (process.env.REACT_APP_MAILCHIMP_SUBSCRIBE_URL) {
      const url =
        process.env.REACT_APP_MAILCHIMP_SUBSCRIBE_URL.replace(
          "/post?",
          "/post-json?"
        ) + `&EMAIL=${this.state.emailAddress}`;
      this.setState({ status: Status.PENDING });

      jsonp(
        url,
        {
          param: "c"
        },
        (err, data) => {
          if (err) {
            this.setState({
              status: Status.REJECTED,
              responseMsg: err
            });
          } else if (data.result !== "success") {
            if (data.msg.includes("subscribed")) {
              this.setState({
                status: Status.FULFILLED,
                responseMsg: null
              });
            } else {
              this.setState({
                status: Status.REJECTED,
                responseMsg: data.msg
              });
            }
          } else {
            this.setState({
              status: Status.FULFILLED,
              responseMsg: data.msg
            });
          }
        }
      );
    }
  };

  getButtonMessage = (): string => {
    const { showErrorMsg, showSuccessMsg } = this.state;

    if (showErrorMsg) {
      return "Something went wrong, try again";
    } else if (showSuccessMsg) {
      return "Subscribed";
    }

    return "Subscribe";
  };

  scrollCheck = () => {
    const footer =
      this.footerRef.current && this.footerRef.current.clientHeight;
    if (footer) {
      const scrollTop =
        window.pageYOffset !== undefined
          ? window.pageYOffset
          : (
              document.documentElement ||
              document.body.parentNode ||
              document.body
            ).scrollTop;
      const docHeight = document.body.scrollHeight;
      const winHeight = window.innerHeight;

      const footerTop = docHeight - footer - winHeight;

      if (scrollTop > footerTop) {
        this.setState({ footerIsActive: true });
      }
    }
  };

  render() {
    const { globalSettings } = this.props;
    const { emailAddress, footerIsActive } = this.state;

    return (
      <div
        ref={this.footerRef as RefObject<HTMLDivElement>}
        className="Footer bg-color-orange flex flex-col items-center"
      >
        <div
          className={cx(
            "Footer__email-subscription-container transition-down--inactive w100 flex-grow flex flex-col items-center justify-between md:justify-center",
            {
              "transition-down--active": footerIsActive
            }
          )}
        >
          <Image
            className="Footer__logo md:none"
            alt="Minniemuse logo"
            src="/assets/images/minniemuse-logo.svg"
          />
          <p className="Footer__email-subscription-text color-white text-bold text-md gt-america text-center uppercase">
            Get Minniemuse straight to your inbox.
          </p>
          <div className="w100 md:mb0 flex flex-col">
            <TextField
              className="Footer__email w100"
              name="email"
              ariaLabel="Enter email"
              variant="primary"
              placeholder="HELLO@EXAMPLE.COM"
              type="email"
              onChange={value =>
                this.setState({ emailAddress: value as string })
              }
              value={emailAddress}
            />
            <Button
              className="Footer__submit-button color-white bg-color-transparent text-bold uppercase gt-america text-sm transition"
              onClick={() => {
                this.subscribe();
              }}
              variant="primary"
              ariaLabel="Subscribe to newsletter"
              label={this.getButtonMessage()}
            />
          </div>
        </div>
        <div className="Footer__links-container w100 flex flex-col md:flex-row items-center justify-between">
          <div className="Footer__left md:col-4 flex flex-col md:flex-row items-center">
            <Button
              className="Footer__link color-white gt-america text-bold text-sm uppercase p0"
              ariaLabel="Navigate to minniemuse.com"
              label="minniemuse.com"
              to={ExternalRoutes.MINNIEMUSE_HOME_LINK}
            />
            <Button
              className="Footer__link color-white gt-america text-bold text-sm uppercase p0"
              ariaLabel="Navigate to faq page"
              label="FAQ"
              to={MinniemuseShopRoutes.FAQ.path}
            />
            <Button
              className="Footer__link color-white bg-color-transparent gt-america text-bold text-sm uppercase p0"
              ariaLabel="Navigate to contact page"
              label="Contact"
              to="mailto:merch@minniemuse.com"
            />
          </div>
          <div className="md:col-4 flex justify-center">
            <Image
              className="Footer__logo none md:block"
              alt="Minniemuse logo"
              src="/assets/images/minniemuse-logo.svg"
            />
          </div>
          <div className="Footer__right md:col-4 flex flex-col md:flex-row items-center md:justify-end">
            <Button
              className="Footer__link color-white gt-america text-bold text-sm uppercase p0"
              ariaLabel="Navigate to terms and conditions page"
              label="Terms &amp; Conditions"
              to={MinniemuseShopRoutes.TERMS_CONDITIONS.path}
            />
            <Button
              className="Footer__link bg-color-transparent p0"
              ariaLabel="Open minniemuse instagram"
              to={globalSettings.instagramLink}
            >
              <Image
                className="Footer__instagram-icon w100"
                alt="Open minniemuse instagram"
                src="/assets/images/instagram-icon.svg"
              />
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState) => ({
  globalSettings: state.applicationUI.globalSettings
});

export default connect(mapStateToProps)(Footer);
